/**
 * Export Utilities
 * Functions for exporting data to various formats
 */

import * as XLSX from 'xlsx';

/**
 * Export data to Excel (.xlsx) file
 * @param {Array} data - Array of objects to export
 * @param {string} filename - Name of the file (without extension)
 * @param {string} sheetName - Name of the worksheet
 */
export const exportToExcel = (data, filename = 'export', sheetName = 'Sheet1') => {
  try {
    // Create a new workbook
    const workbook = XLSX.utils.book_new();

    // Convert data to worksheet
    const worksheet = XLSX.utils.json_to_sheet(data);

    // Auto-size columns
    const columnWidths = [];
    if (data.length > 0) {
      Object.keys(data[0]).forEach((key) => {
        const maxLength = Math.max(
          key.length,
          ...data.map((row) => {
            const value = row[key];
            return value ? value.toString().length : 0;
          })
        );
        columnWidths.push({ wch: Math.min(maxLength + 2, 50) });
      });
      worksheet['!cols'] = columnWidths;
    }

    // Add worksheet to workbook
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

    // Generate Excel file and trigger download
    XLSX.writeFile(workbook, `${filename}.xlsx`);

    return { success: true, message: 'File exported successfully' };
  } catch (error) {
    console.error('Error exporting to Excel:', error);
    return { success: false, message: 'Failed to export file', error };
  }
};

/**
 * Export data to CSV file
 * @param {Array} data - Array of objects to export
 * @param {string} filename - Name of the file (without extension)
 */
export const exportToCSV = (data, filename = 'export') => {
  try {
    // Convert data to worksheet
    const worksheet = XLSX.utils.json_to_sheet(data);

    // Convert worksheet to CSV
    const csv = XLSX.utils.sheet_to_csv(worksheet);

    // Create blob and download
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.csv`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    return { success: true, message: 'File exported successfully' };
  } catch (error) {
    console.error('Error exporting to CSV:', error);
    return { success: false, message: 'Failed to export file', error };
  }
};

/**
 * Export data to JSON file
 * @param {Array} data - Array of objects to export
 * @param {string} filename - Name of the file (without extension)
 */
export const exportToJSON = (data, filename = 'export') => {
  try {
    const jsonString = JSON.stringify(data, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    link.setAttribute('href', url);
    link.setAttribute('download', `${filename}.json`);
    link.style.visibility = 'hidden';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    return { success: true, message: 'File exported successfully' };
  } catch (error) {
    console.error('Error exporting to JSON:', error);
    return { success: false, message: 'Failed to export file', error };
  }
};

/**
 * Format inventory data for export
 * Cleans up the data and formats it for spreadsheet export
 * @param {Array} items - Inventory items
 * @returns {Array} Formatted data
 */
export const formatInventoryForExport = (items) => {
  return items.map((item) => ({
    'SKU': item.sku,
    'Item Name': item.name,
    'Description': item.description || '',
    'Category': item.category,
    'Supplier': item.supplier,
    'Unit Price': item.unitPrice,
    'Current Stock': item.currentStock,
    'Reorder Point': item.reorderPoint,
    'Stock Value': item.unitPrice * item.currentStock,
    'Status': item.status,
    'Created At': item.createdAt || '',
    'Updated At': item.updatedAt || ''
  }));
};
